import React, { useState, useEffect } from 'react'
import { Modal, Breadcrumb, Radio, Input, Select, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import UploadAdModalHolder from '../../styles/uploadAdModalStyle'
import BreadcrumbEllipsis from '../BreadcrumbEllipsis'
import CommonAdForm from './CommonAdForm'
import DynamicAdForm from './DynamicAdForm'
import { HelpIcon } from '../../../../shared/components/HelpTooltipIcon'
import { stripScheme, withHttps } from '../../../../../services/utils'
import { findFolderPath, collapsePath } from '../../utils/folderTree'
import { translateTagName } from '../../../campaignManager/hooks/contentManager/useLibraryContents'
import Colors from '../../../../../themes/Colors'

const { Option } = Select

// The whitelist starts empty: external URLs are optional, so the field shows only
// its "Add URL" button until the user wants one.
const EMPTY_DYNAMIC = { url: '', reloadOnDisplay: false, whitelist: [], preview: null }

// Shell for the ad upload. Owns the destination folder breadcrumb, the mode
// switch, the two fields shared by both modes (name + category), the state of
// each form body and the submit payload. The bodies themselves live in
// CommonAdForm / DynamicAdForm.
const UploadAdModal = ({
  visible,
  folders = [],
  currentTag = null,
  uploading = false,
  categories = [],
  loadingCategories = false,
  isSuperUser = false,
  onSubmit,
  onCancel,
}) => {
  const { t, i18n } = useTranslation()

  // 'file' uploads one or more media files (the original behaviour); 'dynamic'
  // registers a DCO ad. Only super users can pick the second one.
  const [mode, setMode] = useState('file')
  const [files, setFiles] = useState([])
  const [dynamic, setDynamic] = useState(EMPTY_DYNAMIC)
  // Shared by both modes.
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState(null)

  // Files are uploaded to the folder the user is currently standing in
  // (currentTag); the breadcrumb just shows where that is. Empty path = root.
  const path = currentTag != null ? findFolderPath(folders, currentTag) : []

  useEffect(() => {
    if (!visible) return
    setMode('file')
    setFiles([])
    setDynamic(EMPTY_DYNAMIC)
    setName('')
    setCategoryId(null)
  }, [visible])

  const patchDynamic = (patch) => setDynamic((prev) => ({ ...prev, ...patch }))

  const isDynamic = mode === 'dynamic'
  const canSubmit = isDynamic
    ? !!(name.trim() && stripScheme(dynamic.url) && dynamic.preview)
    : files.length > 0

  const handleOk = () => {
    if (!canSubmit) return
    if (isDynamic) {
      // No files travel for a dynamic ad: the URL goes in `file` and the preview
      // image in `preview`. The saga branches on meta.dynamic.
      onSubmit([], {
        categoryId,
        dynamic: {
          name: name.trim(),
          type: dynamic.reloadOnDisplay ? 'url_reload' : 'url',
          url: withHttps(dynamic.url),
          urlWhitelist: dynamic.whitelist.filter((item) => stripScheme(item)).map(withHttps),
          preview: dynamic.preview,
        },
      })
      return
    }
    // Filed under the current folder (currentTag), resolved by the container's
    // uploadAds wrapper. The saga honours `name` only for a single-file upload;
    // bulk uploads keep each file's own name.
    onSubmit(files, { categoryId, name: name.trim() || undefined })
  }

  const okText = isDynamic
    ? t('Upload ad')
    : files.length > 1
    ? t('Upload {{count}} files', { count: files.length })
    : t('Upload file')

  return (
    <Modal
      title={t('Upload new ad')}
      visible={visible}
      okText={okText}
      cancelText={t('Cancel')}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={uploading}
      okButtonProps={{ disabled: !canSubmit }}
      destroyOnClose
    >
      <UploadAdModalHolder>
        {/* Dynamic ads are a super-user-only capability, so non-super users never
            see the choice and stay on the plain file upload. */}
        {isSuperUser && (
          <Radio.Group
            className="mode-radio"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="file">{t('Common ad')}</Radio.Button>
            <Radio.Button value="dynamic">{t('Dynamic Creative Optimization (DCO)')}</Radio.Button>
          </Radio.Group>
        )}
        {/* Indicates the folder the ad will be uploaded to (read-only). */}
        <Breadcrumb className="upload-breadcrumb" separator=">">
          <Breadcrumb.Item>{t('My ads')}</Breadcrumb.Item>
          {collapsePath(path).map((item) =>
            item.ellipsis ? (
              // Informational only: hover shows the hidden folders, no navigation.
              <Breadcrumb.Item key="ellipsis">
                <BreadcrumbEllipsis hidden={item.hidden} className="upload-bc-ellipsis" />
              </Breadcrumb.Item>
            ) : (
              <Breadcrumb.Item key={item.folder.id}>
                {translateTagName(item.folder.name, t)}
              </Breadcrumb.Item>
            ),
          )}
        </Breadcrumb>

        {/* Required for a dynamic ad, an optional override of the file name for a
            common one (the backend derives it otherwise). */}
        <div className="field">
          <p className="field-label">
            {t('Name')}
            {!isDynamic && (
              <Tooltip title={t('Optional. With several files, each ad keeps its own file name.')}>
                <span className="field-help">
                  <HelpIcon />
                </span>
              </Tooltip>
            )}
          </p>
          <Input
            value={name}
            maxLength={255}
            disabled={!isDynamic && files.length > 1}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Name')}
          />
        </div>

        {/* Any ad type can carry a category (LATIN-2850). Optional server-side. */}
        <div className="field">
          <p className="field-label">{t('Categories')}</p>
          <Select
            showSearch
            loading={loadingCategories}
            value={categoryId}
            onChange={setCategoryId}
            placeholder={t('Select a category')}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option key="placeholder" value={null} style={{ color: Colors.textMuted }}>
              {t('None')}
            </Option>
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {i18n.language === 'en' ? category.name_en : category.name_es}
              </Option>
            ))}
          </Select>
        </div>

        {isDynamic ? (
          <DynamicAdForm value={dynamic} onChange={patchDynamic} />
        ) : (
          <CommonAdForm files={files} onChange={setFiles} />
        )}
      </UploadAdModalHolder>
    </Modal>
  )
}

export default UploadAdModal
